import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useSurveyStore } from "@stores/survey/survey";
import { useCurrentUserStore } from "@stores/currentUser";
import useMyFetch from "@stores/useMyFetch";
import { uniqBy } from "@utils/toolBox";

export const useAppearancesStore = defineStore("appearances", () => {
  const { id: surveyId, token: surveyToken } = storeToRefs(useSurveyStore());
  const { user: currentUser } = storeToRefs(useCurrentUserStore());

  // all appearances
  const appearances = ref([]);
  const uniqueAppearances = computed(() => uniqBy(appearances.value, "user_id"));

  // first three bubbles
  const firstThreeAppearances = computed(() => uniqueAppearances.value.slice(0, 3));

  // +n bubble
  const hasMoreThanThreeAppearances = computed(() => uniqueAppearances.value.length > 3);
  const hasMoreThanFourAppearances = computed(() => uniqueAppearances.value.length > 4);
  const otherAppearances = computed(() => uniqueAppearances.value.slice(3));
  const otherAppearancesLength = computed(() => otherAppearances.value.length);
  const otherAppearancesFullNames = computed(() => otherAppearances.value.map((user) => user.full_name).join(", "));

  // fetch
  const fetchAppearances = async () => {
    try {
      const { data } = await useMyFetch(`/surveys/${surveyToken.value}/appearances`).json();
      appearances.value = data.value;
    } catch (err) {
      console.error(err);
    }
  };

  // channel handlers
  const channelHandlers = (cable) => {
    return {
      connected: appearOnChannel(cable),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
      disconnected: disappearFromChannel(cable),
    };
  };

  const appearOnChannel = (cable) => () =>
    cable.perform({
      channel: "AppearanceChannel",
      action: "appear",
      data: { survey_id: surveyId.value },
    });
  const disappearFromChannel = (cable) => () =>
    cable.perform({
      channel: "AppearanceChannel",
      action: "disappear",
      data: { survey_id: surveyId.value },
    });
  const handleReceivedMessage = (msg) => {
    const data = JSON.parse(msg.data);
    if (data.user_id === currentUser.value.id) return;

    if (msg.action === "appear") appearInMyBubbles(data);
    if (msg.action === "disappear") disappearFromMyBubbles(data);
  };

  const appearInMyBubbles = (user) => (appearances.value = [...appearances.value, user]);
  const disappearFromMyBubbles = (user) =>
    (appearances.value = appearances.value.filter((appearance) => appearance.id !== user.id));

  return {
    // first three bubbles
    firstThreeAppearances,
    // +n bubble
    hasMoreThanThreeAppearances,
    hasMoreThanFourAppearances,
    otherAppearancesLength,
    otherAppearancesFullNames,
    // init
    fetchAppearances,
    // handle channel
    surveyId,
    channelHandlers,
  };
});
